import { formatCurrency } from './utils'
import type { ExpressOrder } from './express-hooks'

// ──────────────────────────────────────────────────────────────────
// Mensaje de WhatsApp para el repartidor (Emilius Pizza).
//
// Se arma con los datos que YA tiene el pedido — nombre del cliente,
// dirección, número de orden y método de pago. No escribe nada en la
// base ni cambia el estado del pedido: solo abre WhatsApp con el texto
// listo, así que se puede reenviar las veces que haga falta.
//
// El monto a cobrar SOLO aparece cuando el cliente eligió efectivo. Si
// pagó por tarjeta o SINPE, el mensaje lo dice explícitamente para que
// el repartidor no cobre de más.
// ──────────────────────────────────────────────────────────────────

/** Métodos en los que el repartidor SÍ recibe dinero en la puerta. */
const CASH_METHODS = ['cash', 'efectivo', 'contra_entrega', 'cash_on_delivery']

export function isCashOnDelivery(method: string | null): boolean {
  return CASH_METHODS.includes(String(method || '').toLowerCase())
}

function paymentLabel(method: string | null): string {
  switch (String(method || '').toLowerCase()) {
    case 'card':
    case 'tarjeta':
      return 'Tarjeta'
    case 'sinpe':
    case 'sinpe_movil':
      return 'SINPE Móvil'
    case 'online':
      return 'Pago en línea'
    default:
      return 'Pagado'
  }
}

/**
 * Deja el teléfono en el formato que espera wa.me: solo dígitos, con
 * código de país. Los números de Costa Rica se guardan a 8 dígitos, así
 * que se les antepone 506.
 */
export function toWhatsappNumber(phone: string | null): string | null {
  const digits = String(phone || '').replace(/\D/g, '')
  if (!digits) return null
  if (digits.length === 8) return `506${digits}`
  if (digits.startsWith('506')) return digits
  return digits
}

export function buildCourierMessage(order: ExpressOrder): string {
  const lines: string[] = []
  lines.push('🛵 *Entrega — Emilius Pizza*')
  lines.push('')
  lines.push(`*Pedido:* #${order.order_number || order.id}`)
  lines.push(`*Cliente:* ${order.customer_name?.trim() || 'Sin nombre'}`)

  if (order.customer_phone) {
    lines.push(`*Teléfono:* ${order.customer_phone}`)
  }
  if (order.delivery_address) {
    lines.push(`*Dirección:* ${order.delivery_address}`)
  }
  if (order.delivery_zone) {
    lines.push(`*Zona:* ${order.delivery_zone}`)
  }

  const mapsHref =
    order.maps_url ||
    (order.delivery_lat && order.delivery_lng
      ? `https://www.google.com/maps/search/?api=1&query=${order.delivery_lat},${order.delivery_lng}`
      : null)
  if (mapsHref) {
    lines.push(`*Ubicación:* ${mapsHref}`)
  }

  lines.push('')
  if (isCashOnDelivery(order.payment_method)) {
    lines.push(`💵 *COBRAR EN EFECTIVO: ${formatCurrency(order.total)}*`)
  } else {
    lines.push(
      `✅ Ya está pagado (${paymentLabel(order.payment_method)}) — no hay que cobrar nada.`,
    )
  }

  return lines.join('\n')
}

/**
 * Enlace de WhatsApp con el mensaje precargado. Si el repartidor no
 * tiene teléfono registrado, se abre WhatsApp sin destinatario para que
 * el encargado elija el contacto a mano.
 */
export function buildCourierWhatsappHref(
  order: ExpressOrder,
  courierPhone: string | null,
): string {
  const text = encodeURIComponent(buildCourierMessage(order))
  const number = toWhatsappNumber(courierPhone)
  return number
    ? `https://wa.me/${number}?text=${text}`
    : `https://wa.me/?text=${text}`
}
